import type { InfraHubRuntimeConfig } from '../runtime/runtime-config.js';
import {
  buildAuthorizationUrl,
  generateOAuthState,
  parseAuthorizationCallback,
} from './oauth-authorization.js';
import { createPkceS256Challenge, generatePkceVerifier } from './pkce.js';
import { createBrowserAuthorizationRequestStore } from './session-storage-authorization-request-store.js';

export interface OAuthSession {
  readonly accessToken: string;
  readonly refreshToken?: string | undefined;
  readonly expiresAt: number;
}

type TokenResponse = Readonly<{
  access_token?: unknown;
  refresh_token?: unknown;
  expires_in?: unknown;
}>;

const MAX_PENDING_AGE_MS = 10 * 60 * 1000;

export class InfraHubOAuthClient {
  readonly #config: InfraHubRuntimeConfig;
  readonly #fetch: typeof fetch;
  #session: OAuthSession | undefined;

  constructor(config: InfraHubRuntimeConfig, fetchApi: typeof fetch = globalThis.fetch) {
    this.#config = config;
    this.#fetch = fetchApi;
  }

  get session(): OAuthSession | undefined {
    return this.#session;
  }

  async beginAuthorization(now = Date.now()): Promise<string> {
    const state = generateOAuthState();
    const codeVerifier = generatePkceVerifier();
    const codeChallenge = await createPkceS256Challenge(codeVerifier);
    createBrowserAuthorizationRequestStore().save({
      state,
      codeVerifier,
      redirectUri: this.#config.oauth.redirectUri,
      createdAt: now,
    });

    return buildAuthorizationUrl(
      {
        authorizationEndpoint: this.#config.oauth.authorizationEndpoint,
        clientId: this.#config.oauth.clientId,
        redirectUri: this.#config.oauth.redirectUri,
        scopes: this.#config.oauth.scopes,
      },
      state,
      codeChallenge,
    );
  }

  async completeAuthorization(callbackUrl: string, now = Date.now()): Promise<OAuthSession> {
    const callback = new URL(callbackUrl);
    const state = callback.searchParams.get('state');
    if (state === null) throw new Error('OAuth callback state is missing.');

    const pending = createBrowserAuthorizationRequestStore().consume(state);
    if (pending === undefined) {
      throw new Error('OAuth callback has no pending authorization request.');
    }
    if (now - pending.createdAt > MAX_PENDING_AGE_MS) {
      throw new Error('OAuth authorization request expired.');
    }

    const result = parseAuthorizationCallback(callbackUrl, pending.state);
    this.#session = await this.#exchange(
      new URLSearchParams({
        grant_type: 'authorization_code',
        client_id: this.#config.oauth.clientId,
        code: result.code,
        redirect_uri: pending.redirectUri,
        code_verifier: pending.codeVerifier,
      }),
      now,
    );
    return this.#session;
  }

  async refresh(now = Date.now()): Promise<OAuthSession> {
    const refreshToken = this.#session?.refreshToken;
    if (refreshToken === undefined) {
      this.#session = undefined;
      throw new Error('No refresh token is available.');
    }

    try {
      const next = await this.#exchange(
        new URLSearchParams({
          grant_type: 'refresh_token',
          client_id: this.#config.oauth.clientId,
          refresh_token: refreshToken,
        }),
        now,
      );
      this.#session = {
        ...next,
        refreshToken: next.refreshToken ?? refreshToken,
      };
      return this.#session;
    } catch (error) {
      this.#session = undefined;
      throw error;
    }
  }

  async authorizedFetch(path: string, init: RequestInit = {}, now = Date.now()): Promise<Response> {
    if (this.#session === undefined || this.#session.expiresAt <= now) {
      this.#session = undefined;
      throw new Error('An active OAuth session is required.');
    }

    const url = new URL(path, this.#config.apiBaseUrl);
    if (url.origin !== new URL(this.#config.apiBaseUrl).origin) {
      throw new Error('Authenticated requests must target the configured API origin.');
    }

    const headers = new Headers(init.headers);
    headers.set('Authorization', `Bearer ${this.#session.accessToken}`);
    return this.#fetch(url, { ...init, headers });
  }

  async revokeAndClear(): Promise<void> {
    const refreshToken = this.#session?.refreshToken;
    this.#session = undefined;
    if (refreshToken === undefined) return;

    await this.#fetch(this.#config.oauth.revocationEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: new URLSearchParams({
        token: refreshToken,
        client_id: this.#config.oauth.clientId,
      }),
    });
  }

  buildLogoutUrl(): string {
    this.#session = undefined;
    const url = new URL(this.#config.oauth.logoutEndpoint);
    url.searchParams.set('client_id', this.#config.oauth.clientId);
    url.searchParams.set('logout_uri', this.#config.oauth.postLogoutRedirectUri);
    return url.toString();
  }

  async #exchange(body: URLSearchParams, now: number): Promise<OAuthSession> {
    const response = await this.#fetch(this.#config.oauth.tokenEndpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body,
    });
    if (!response.ok) {
      throw new Error(`OAuth token endpoint returned HTTP ${response.status}.`);
    }

    const token = (await response.json()) as TokenResponse;
    if (
      typeof token.access_token !== 'string' ||
      token.access_token.length === 0 ||
      typeof token.expires_in !== 'number' ||
      token.expires_in <= 0
    ) {
      throw new Error('OAuth token response is malformed.');
    }
    if (token.refresh_token !== undefined && typeof token.refresh_token !== 'string') {
      throw new Error('OAuth refresh token is malformed.');
    }

    return {
      accessToken: token.access_token,
      refreshToken: token.refresh_token,
      expiresAt: now + token.expires_in * 1000,
    };
  }
}
